import { useCallback, useState } from "react"

import { useUpdateGoodsReceipt } from "./useUpdateGoodsReceipt"

import type { GoodsReceipt, Order } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import type { GoodsReceiptActionState } from "@/screens/WarehouseReceiving/types"
import { GoodReceiptActionType } from "@/screens/WarehouseReceiving/types"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseGoodsReceiptActionsProps {
  goodsReceipt?: GoodsReceipt | null
  selectedPurchaseOrders?: Order[] | { orderId: number }[]
  onRefetch?: () => void
  addPurchaseOrderSuccessMessage?: string
}

interface UseGoodsReceiptActionsReturn {
  actionState: GoodsReceiptActionState
  purchaseOrderToRemove: number | null
  handleDialogClose: () => void
  handleEditGoodsReceipt: (goodsReceipt: GoodsReceipt) => void
  handleAddPurchaseOrder: () => void
  handleConfirmAddPurchaseOrder: () => Promise<void>
  handleRemovePurchaseOrder: (purchaseOrderId: number) => void
  handlePurchaseOrderRemoveClose: () => void
  handlePurchaseOrderRemoveSuccess: () => void
  handleFormDialogSubmit: () => void
  handleUploadFiles: () => void
  setActionState: (state: GoodsReceiptActionState) => void
}

export const useGoodsReceiptActions = ({
  goodsReceipt,
  selectedPurchaseOrders,
  onRefetch,
  addPurchaseOrderSuccessMessage,
}: UseGoodsReceiptActionsProps): UseGoodsReceiptActionsReturn => {
  const [actionState, setActionState] = useState<GoodsReceiptActionState>(null)
  const [purchaseOrderToRemove, setPurchaseOrderToRemove] = useState<number | null>(null)

  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { update } = useUpdateGoodsReceipt({
    onSuccess: onRefetch,
  })
  const { update: updateWithCustomMessage } = useUpdateGoodsReceipt({
    onSuccess: onRefetch,
    successMessage: addPurchaseOrderSuccessMessage,
  })

  const handleDialogClose = useCallback(() => {
    setActionState(null)
  }, [])

  const handleEditGoodsReceipt = useCallback((goodsReceipt: GoodsReceipt) => {
    setActionState({ type: GoodReceiptActionType.Form, goodsReceipt })
  }, [])

  const handleAddPurchaseOrder = useCallback(() => {
    setActionState({ type: GoodReceiptActionType.AddPurchaseOrder })
  }, [])

  const handleConfirmAddPurchaseOrder = useCallback(async () => {
    try {
      if (!goodsReceipt || !selectedPurchaseOrders?.length) return

      if (selectedPurchaseOrders.length === 0) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: "Please select at least one Purchase Order.",
        })
        return
      }

      const currentOrderIds = goodsReceipt.orders?.map((order) => order.orderId) || []
      const selectedOrderIds = selectedPurchaseOrders.map((po) => po.orderId)
      const allOrderIds = [...new Set([...currentOrderIds, ...selectedOrderIds])]

      const updateFunction = addPurchaseOrderSuccessMessage ? updateWithCustomMessage : update
      await updateFunction({
        goodsReceiptId: goodsReceipt.goodsReceiptId,
        orderIds: allOrderIds,
        warehouseId: goodsReceipt.warehouseArea?.warehouseAreaId as string,
      })
    } finally {
      handleDialogClose()
    }
  }, [
    goodsReceipt,
    selectedPurchaseOrders,
    update,
    updateWithCustomMessage,
    addPurchaseOrderSuccessMessage,
    notify,
    handleDialogClose,
  ])

  const handleRemovePurchaseOrder = useCallback((purchaseOrderId: number) => {
    setPurchaseOrderToRemove(purchaseOrderId)
  }, [])

  const handlePurchaseOrderRemoveClose = useCallback(() => {
    setPurchaseOrderToRemove(null)
  }, [])

  const handlePurchaseOrderRemoveSuccess = useCallback(() => {
    onRefetch?.()
  }, [onRefetch])

  const handleFormDialogSubmit = useCallback(() => {
    handleDialogClose()
    onRefetch?.()
  }, [handleDialogClose, onRefetch])

  const handleUploadFiles = useCallback(() => {
    setActionState({ type: GoodReceiptActionType.FileUpload })
  }, [])

  return {
    actionState,
    purchaseOrderToRemove,
    handleDialogClose,
    handleEditGoodsReceipt,
    handleAddPurchaseOrder,
    handleConfirmAddPurchaseOrder,
    handleRemovePurchaseOrder,
    handlePurchaseOrderRemoveClose,
    handlePurchaseOrderRemoveSuccess,
    handleFormDialogSubmit,
    handleUploadFiles,
    setActionState,
  }
}
